import React, { useEffect, useState } from 'react';

import { Box } from '@mui/material';
import dayjs from 'dayjs';
import _ from 'lodash';

import DataGrid from '../../../components/DataGrid';
import { useGetExamMarkingRequestsQuery } from '../../../services/exams';
import { ExamMarkingRequest } from '../../../services/exams/types';
import { ExamRequestStatus } from '../../../types/common';
import { SUBJECTS } from '../../../utils/common/constants';
import { DueDateText } from '../index.styles';

interface RequestsTabProps {
  marker?: string;
  selectedRequestId?: string,
  statuses: string[];
  onRequestClick?: (requestId: string) => void;
}

const RequestsTab = ({
  marker, statuses, selectedRequestId, onRequestClick = () => {},
}: RequestsTabProps) => {
  const [mappedRequests, setMappedRequests] = useState<ExamMarkingRequest[] | undefined>(undefined);

  const { data: requests, isLoading: isRequestsLoading } = useGetExamMarkingRequestsQuery({
    marker,
    status: statuses.length === 1 ? statuses[0] : undefined,
  });

  useEffect(() => {
    if (requests?.data) {
      let filtered = [];

      if (statuses.length > 1) {
        filtered = requests.data.filter((request) => statuses.includes(request.status));
      } else {
        // If only one status is provided, no need to filter as the query is made
        // with the status as a parameter
        filtered = requests.data;
      }

      const mapped = filtered.map((entry) => ({
        ...entry,
        displayedExamLabel: (entry.examSubject && entry.examLabel) ? `${SUBJECTS[entry.examSubject].label} ${entry.examLabel}` : '???',
        displayedStudentName: entry.studentName || '???',
        displayedMarkerName: entry.markerName || '???',
      }));

      setMappedRequests(mapped);
    }
  }, [requests]);

  return (
    <Box display="grid" width="100%">
      <DataGrid
        selectedRowId={selectedRequestId}
        initialState={{
          sorting: {
            sortModel: statuses.includes(ExamRequestStatus.PendingAcceptance) ? [{
              field: 'dueDatetime', sort: 'asc',
            }] : [{
              field: 'creationDatetime', sort: 'desc',
            }],
          },
        }}
        loading={isRequestsLoading || !mappedRequests}
        getRowHeight={() => 40}
        onRowClick={({ row }) => onRequestClick(row.requestId)}
        columns={[
          {
            field: 'creationDatetime',
            headerName: 'Created at',
            width: 200,
            renderCell: ({ row } : any) => (
              dayjs(row.creationDatetime).format('DD MMM YYYY hh:mm a')
            ),
          },
          {
            field: 'dueDatetime',
            headerName: 'Due',
            width: 140,
            renderCell: ({ row } : any) => (
              <DueDateText $overdue={row?.dueDatetime && row.dueDatetime < dayjs().valueOf()}>
                {row.dueDatetime ? _.upperFirst(dayjs(row.dueDatetime).fromNow()) : '-'}
              </DueDateText>
            ),
            sortComparator: (v1: number, v2: number) => {
              const current = dayjs().valueOf();
              return ((v1 ?? Infinity) - current) - ((v2 ?? Infinity) - current);
            },
          },
          {
            field: 'displayedExamLabel',
            headerName: 'Exam',
            width: 320,
          },
          {
            field: 'displayedStudentName',
            headerName: 'Student',
            width: 160,
          },
          {
            field: 'displayedMarkerName',
            headerName: 'Marker',
            width: 240,
            renderCell: ({ row } : any) => (
              `${row.displayedMarkerName}${row.status === ExamRequestStatus.PendingReassignment ? ' (Dropped)' : ''}`
            ),
          },
        ]}
        rows={mappedRequests || []}
      />
    </Box>
  );
};

export default RequestsTab;
